import { APIConfig } from '../config/APIConfig';
import { buildApiUrl } from '../../config/environment';

export interface HistoricalImportPreview {
  rows_detected: number;
  date_range_start: string;
  date_range_end: string;
  total_people: number;
  sample_rows: Array<{ date: string; people_count: number }>;
}

export interface HistoricalImportRecord {
  id: string;
  source_system: string;
  period_label: string;
  imported_by: string;
  rows_imported: number;
  imported_at: string;
}

export interface MonthlyCombinedData {
  month: string;
  month_name: string;
  people_count: number;
  people_awflux: number;
  people_historical: number;
  detections_count: number;
  source: 'awflux' | 'historical' | 'mixed';
}

export interface MonthlyCombinedResponse {
  months: MonthlyCombinedData[];
  has_historical_data: boolean;
  generated_at: string;
}

export class HistoricalImportService {
  /**
   * Preview da planilha antes de importar
   */
  static async preview(file: File): Promise<HistoricalImportPreview> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await APIConfig.getInstance().post<HistoricalImportPreview>(
      buildApiUrl('/api/v1/historical-imports/preview'),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      }
    );
    return response.data;
  }

  /**
   * Importar planilha para o banco
   */
  static async upload(
    file: File,
    sourceSystem: string,
    periodLabel: string
  ): Promise<{ message: string; import_id: string; rows_imported: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_system', sourceSystem);
    formData.append('period_label', periodLabel);

    const response = await APIConfig.getInstance().post(
      buildApiUrl('/api/v1/historical-imports/upload'),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      }
    );
    return response.data;
  }

  /**
   * Listar importações da organização (backend usa org do JWT)
   */
  static async list(): Promise<HistoricalImportRecord[]> {
    const response = await APIConfig.getInstance().get<HistoricalImportRecord[]>(
      buildApiUrl('/api/v1/historical-imports')
    );
    return response.data;
  }

  /**
   * Deletar uma importação e seus dados
   */
  static async delete(importId: string): Promise<void> {
    await APIConfig.getInstance().delete(
      buildApiUrl(`/api/v1/historical-imports/${importId}`)
    );
  }

  /**
   * Buscar dados mensais combinados (AWFlux + histórico)
   * cameraIds (múltiplas câmeras, ex: zonas de mapa de calor) sobrescreve cameraId.
   */
  static async getMonthlyCombined(
    months: number = 24,
    cameraId?: string,
    cameraIds?: string[]
  ): Promise<MonthlyCombinedResponse> {
    let url = `/api/v1/stats/monthly-combined?months=${months}`;
    if (cameraId) {
      url += `&camera_id=${cameraId}`;
    } else if (cameraIds && cameraIds.length > 0) {
      url += `&camera_ids=${cameraIds.join(',')}`;
    }
    const response = await APIConfig.getInstance().get<MonthlyCombinedResponse>(
      buildApiUrl(url),
      { timeout: 90000 }
    );
    return response.data;
  }

  /**
   * Buscar contagens históricas diárias para um período (ano anterior)
   */
  static async getWeeklyHistorical(startDate: string, endDate: string): Promise<WeeklyHistoricalResponse> {
    const response = await APIConfig.getInstance().get<WeeklyHistoricalResponse>(
      buildApiUrl(`/api/v1/stats/weekly-historical?start_date=${startDate}&end_date=${endDate}`)
    );
    return response.data;
  }
}

export interface WeeklyHistoricalData {
  date: string;
  people_count: number;
}

export interface WeeklyHistoricalResponse {
  days: WeeklyHistoricalData[];
  has_data: boolean;
}
